import { Badge, Box, List, Typography } from '@mui/material'
import { ReactNode, useMemo } from 'react'
import { Divider, IconButton, Icons, MenuListItem } from 'utoniq-ui'

import { BaseLayout, Head } from '@/components/layouts'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'
import { individualHideDelivery } from '@/core/individual'
import { filterHiddenItem } from '@/lib/array'

export * from './fragments'

export type State = {
  hasUnappliedDeliveryItems: boolean
  isMembershipPersonalInformationRequired: boolean
  hasWaitingPaymentItems: boolean
  hasWaitingResaleRafflePaymentItems: boolean
}

export type Action = {}

type Item = {
  title: string
  icon: ReactNode
  href: string
  notificationText?: string
}

export const ManagementTemplate: Template<State, Action> = ({ state }) => {
  const { t } = useTranslation('management')
  const { routerPages, generatePath, routerNames } = useRouter()

  const items = useMemo<Item[]>(
    () => [
      {
        icon: <Icons.Delivery fontSize="medium" />,
        title: t('deliveryManagement'),
        hidden: individualHideDelivery(),
        href: routerPages.DeliveryManagement,
        notificationText: state.hasUnappliedDeliveryItems
          ? t('deliveryManagementNotification')
          : '',
      },
      {
        icon: <Icons.MemberShip fontSize="medium" />,
        title: t('membershipManagement'),
        hidden: !config.modules.membership,
        href: routerPages.MembershipManagement,
        notificationText: state.isMembershipPersonalInformationRequired
          ? t('membershipManagementNotification')
          : '',
      },
      {
        icon: <Icons.CartHistory fontSize="medium" />,
        title: t('storePurchaseOrders'),
        href: routerPages.StorePurchaseOrders,
        hidden: !config.modules.cart,
      },
      {
        icon: <Icons.PurchaseHistory fontSize="medium" />,
        title: t('purchaseHistory'),
        hidden: !config.modules.item,
        href: routerPages.ItemOrders,
      },
      {
        icon: <Icons.Lottery fontSize="medium" />,
        title: t('lotteryApplicants'),
        hidden: !config.modules.preSale,
        href: routerPages.LotteryApplications,
      },
      {
        hidden: !config.modules.cart && !config.modules.preSale,
        icon: <Icons.Wallet fontSize="medium" />,
        title: t('paymentStatuses'),
        href: routerPages.PaymentStatuses,
        notificationText: state.hasWaitingPaymentItems
          ? t('UnsettledItemListNotification')
          : '',
      },
      {
        hidden: !config.modules.resale,
        icon: <Icons.Resale fontSize="medium" />,
        title: t('resale'),
        href: generatePath(routerPages.ResaleManagement),
        notificationText: state.hasWaitingResaleRafflePaymentItems
          ? t('resaleNote')
          : '',
      },
    ],
    [
      t,
      routerPages.DeliveryManagement,
      routerPages.MembershipManagement,
      routerPages.StorePurchaseOrders,
      routerPages.ItemOrders,
      routerPages.LotteryApplications,
      routerPages.PaymentStatuses,
      routerPages.ResaleManagement,
      state.hasUnappliedDeliveryItems,
      state.isMembershipPersonalInformationRequired,
      state.hasWaitingPaymentItems,
      state.hasWaitingResaleRafflePaymentItems,
      generatePath,
    ]
  )

  return (
    <BaseLayout
      header={{
        left: (
          <IconButton.Back
            edge="start"
            size="medium"
            href={routerPages.GlobalHome}
          />
        ),
        title: t('title'),
      }}
    >
      <Head title={routerNames.Management} />
      <List>
        {filterHiddenItem(items).map((item, index) => (
          <Box key={index} sx={{ position: 'relative' }}>
            <MenuListItem
              sx={{
                backgroundColor: 'grey.50',
                py: 2,
                px: 5,
                height: { xs: 52, md: 56 },
              }}
              title={item.title}
              icon={item.icon}
              href={item.href}
              primaryTypographyProps={{
                variant: 'body1',
              }}
              secondaryAction={
                <Typography variant="overline" color="like.tap">
                  {item.notificationText}
                </Typography>
              }
            />
            {item.notificationText && (
              <Badge
                badgeContent=""
                color={'like' as any}
                sx={{
                  position: 'absolute',
                  top: '18px',
                  left: '32px',
                  width: 12,
                  height: 12,
                  transform: 'scale(0.9)',
                  '& > span': {
                    minWidth: 12,
                    height: 12,
                    bgcolor: 'like.tap',
                  },
                }}
              />
            )}
            {items.length - 1 > index && <Divider />}
          </Box>
        ))}
      </List>
    </BaseLayout>
  )
}
